/**
 * Build a deposit's history and watch the balance follow.
 *
 * This widget calls the real depositPaidAud() from src/utils/bookingSummary.ts
 * rather than reimplementing it, so whatever the app believes a deposit is
 * worth is exactly what this page shows. The lesson it teaches: a deposit is
 * not a number on a record, it is the sum of everything that ever happened to
 * it, and two of those movements are deliberately not counted.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { depositPaidAud } from '@/utils/bookingSummary';
import type { Deposit, DepositLedgerEntry } from '@/domain/types';
import { Callout, Playground, Stat } from './primitives';
import { money } from './format';

const ASKED_FOR = 400;

type MoveKey = 'topup' | 'rolloverOut' | 'forfeit' | 'reinstate' | 'refunded';

const MOVES: Record<MoveKey, { label: string; amount: number; kind: DepositLedgerEntry['kind']; note: string }> = {
  topup: { label: 'Client tops up $150', amount: 150, kind: 'topup', note: 'Paying off the rest in instalments.' },
  rolloverOut: {
    label: 'Roll forward to a new booking',
    amount: 0,
    kind: 'rollover-out',
    note: 'They rebooked, so the pot moves to the next sitting.',
  },
  forfeit: { label: 'Client no-shows', amount: 0, kind: 'forfeit', note: 'The studio keeps the money.' },
  reinstate: {
    label: 'Manager reinstates it',
    amount: 0,
    kind: 'reinstate',
    note: 'Their car broke down. Give it back.',
  },
  refunded: { label: 'Refund the client', amount: 0, kind: 'refunded', note: 'Money returned out of band.' },
};

const KIND_LABEL: Record<DepositLedgerEntry['kind'], string> = {
  paid: 'Paid',
  topup: 'Top up',
  'rollover-in': 'Rolled in',
  'rollover-out': 'Rolled out',
  forfeit: 'Forfeited',
  reinstate: 'Reinstated',
  redeemed: 'Redeemed',
  refunded: 'Refunded',
  'transfer-in': 'Transferred in',
  'transfer-out': 'Transferred out',
};

const COUNTED: Record<DepositLedgerEntry['kind'], 1 | -1 | 0> = {
  paid: 1,
  topup: 1,
  'rollover-in': 1,
  reinstate: 1,
  'rollover-out': -1,
  forfeit: -1,
  // Redemption is an explicit "used up" marker, not a paid-balance movement
  // (depositPaidAud ignores it too), so it does not change the counted total.
  redeemed: 0,
  refunded: 0,
  'transfer-in': 0,
  'transfer-out': 0,
};

const OPENING: DepositLedgerEntry[] = [
  { at: '2026-04-02T10:15:00+10:00', amountAud: 250, kind: 'paid', note: 'Deposit link paid on the phone.' },
];

/** A structurally complete deposit so the real balance helper can read it. */
function asDeposit(ledger: DepositLedgerEntry[]): Deposit {
  return {
    id: 'demo-deposit',
    customerId: 'demo-customer',
    studioId: 'coburg',
    amountAud: ASKED_FOR,
    feesAud: 0,
    netAud: ASKED_FOR,
    status: 'paid',
    channelSent: 'sms',
    stripePaymentLinkId: 'demo-link',
    expiresAt: '2026-04-04T10:15:00+10:00',
    createdAt: '2026-04-02T09:00:00+10:00',
    policy: 'seven-day-rule',
    source: 'web-form',
    ledger,
  };
}

export default function DepositLedgerPlayground() {
  const [ledger, setLedger] = useState<DepositLedgerEntry[]>(OPENING);

  const balance = depositPaidAud(asDeposit(ledger));
  const outstanding = Math.max(0, ASKED_FOR - balance);

  const add = (key: MoveKey) => {
    const move = MOVES[key];
    // Forfeit, reinstate and roll-forward all move whatever the balance
    // happens to be, which is what keeps the ledger from ever going negative.
    const amount = move.amount > 0 ? move.amount : balanceForMove(key, ledger, balance);
    if (amount <= 0) return;
    setLedger((prev) => [
      ...prev,
      { at: `2026-04-0${Math.min(9, prev.length + 3)}T12:00:00+10:00`, amountAud: amount, kind: move.kind, note: move.note },
    ]);
  };

  const isForfeited = lastIndexOfKind(ledger, 'forfeit') > lastIndexOfKind(ledger, 'reinstate');

  return (
    <Playground
      title="What is this deposit actually worth?"
      instruction="Add things that happen to a deposit over its life. The balance underneath is calculated by the same function the rest of the app uses."
      footnote="The client was asked for $400 and paid $250 up front, which is what makes this a partial deposit. Partial is never a flag anyone sets, it is simply what the sum comes to."
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
        <div className="space-y-2">
          <p className="label-small-caps text-cream-soft">Make something happen</p>
          {(Object.keys(MOVES) as MoveKey[]).map((k) => {
            const disabled =
              (k === 'reinstate' && !isForfeited) ||
              (k !== 'reinstate' && k !== 'topup' && balance <= 0) ||
              (k === 'forfeit' && isForfeited);
            return (
              <button
                key={k}
                type="button"
                disabled={disabled}
                onClick={() => add(k)}
                className={clsx(
                  'w-full rounded-md border px-3.5 py-2.5 text-left font-inter text-sm transition',
                  disabled
                    ? 'border-gold-dim/15 text-cream-faint/50 cursor-not-allowed'
                    : 'border-gold-dim/35 text-cream hover:border-gold-bright hover:bg-gold/10',
                )}
              >
                {MOVES[k].label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setLedger(OPENING)}
            className="btn-quiet mt-2 w-full justify-center"
          >
            Start again
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2.5">
            <Stat label="Asked for" value={money(ASKED_FOR)} />
            <Stat
              label="Worth today"
              value={money(balance)}
              strong
              tone={balance === 0 ? 'blood' : 'gold'}
            />
            <Stat
              label="Still owing"
              value={money(outstanding)}
              tone={outstanding > 0 ? 'orange' : 'mint'}
              caption={outstanding > 0 ? 'Chased weekly.' : 'Paid in full.'}
            />
          </div>

          <div className="overflow-hidden rounded-md border border-gold-dim/20">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-ink-dark/70">
                  <th className="label-small-caps text-cream-faint px-3.5 py-2">What happened</th>
                  <th className="label-small-caps text-cream-faint px-3.5 py-2 text-right">Amount</th>
                  <th className="label-small-caps text-cream-faint px-3.5 py-2 text-right">Counts?</th>
                  <th className="label-small-caps text-cream-faint px-3.5 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((e, i) => {
                  const running = depositPaidAud(asDeposit(ledger.slice(0, i + 1)));
                  const sign = COUNTED[e.kind];
                  return (
                    <tr key={`${e.kind}-${i}`} className="border-t border-gold-dim/15">
                      <td className="px-3.5 py-2.5">
                        <span className="font-inter text-sm text-cream">{KIND_LABEL[e.kind]}</span>
                        {e.note ? (
                          <span className="block font-serif text-xs text-cream-faint mt-0.5">{e.note}</span>
                        ) : null}
                      </td>
                      <td className="data-mono tabular-nums px-3.5 py-2.5 text-right text-sm text-cream-soft">
                        {sign === -1 ? '-' : sign === 1 ? '+' : ''}
                        {money(e.amountAud)}
                      </td>
                      <td className="px-3.5 py-2.5 text-right">
                        {sign === 0 ? (
                          <span className="font-inter text-[11px] uppercase tracking-wider text-cream-faint">No</span>
                        ) : (
                          <span className="font-inter text-[11px] uppercase tracking-wider text-mint">Yes</span>
                        )}
                      </td>
                      <td className="data-mono tabular-nums px-3.5 py-2.5 text-right text-sm text-cream">
                        {money(running)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {ledger.some((e) => e.kind === 'refunded') ? (
            <Callout tone="azure" title="Why the refund did not change the balance">
              A refund is money leaving the business, not a movement on what the client has on file. It is tracked, but
              it deliberately sits outside this sum so a refunded deposit never reads as credit the client can spend.
            </Callout>
          ) : null}

          {isForfeited ? (
            <Callout tone="blood" title="Forfeited, and reversible">
              The forfeit is written as the exact balance at that moment, which is why the total lands on zero rather
              than going negative. Nothing is deleted. A manager can reinstate it, and the deposit goes straight back
              onto the board looking for a date.
            </Callout>
          ) : null}

          {ledger.some((e) => e.kind === 'rollover-out') ? (
            <Callout tone="mint" title="Rolled forward, not spent">
              This record is now empty because the money moved to a new deposit against the next booking. Both halves
              of the pair are kept so the trail is intact, and the roll is excluded from money-in reporting so the same
              cash is never banked twice.
            </Callout>
          ) : null}
        </div>
      </div>
    </Playground>
  );
}

function lastIndexOfKind(ledger: DepositLedgerEntry[], kind: DepositLedgerEntry['kind']): number {
  let found = -1;
  ledger.forEach((e, i) => {
    if (e.kind === kind) found = i;
  });
  return found;
}

/** Forfeit, reinstate and roll-forward all move the whole current balance. */
function balanceForMove(key: MoveKey, ledger: DepositLedgerEntry[], balance: number): number {
  if (key === 'reinstate') {
    const idx = lastIndexOfKind(ledger, 'forfeit');
    return idx >= 0 ? ledger[idx]!.amountAud : 0;
  }
  return balance;
}
